import { Injectable } from '@angular/core';
import Bugsnag, { NotifiableError } from '@bugsnag/js';
import { environment } from 'environments/environment';
import { ErrorReportingPort } from '../error-reporting.port';
import { AppError } from '../../services/app-error';
import {
    ErrorReporterBreadcrumbType,
    ErrorReporterMetadata,
    ErrorReporterOptions,
    ErrorReporterUser,
} from '@app/common/observability/domain/services/error-reporter.service';
import {
    ErrorReportFilterInput,
    ErrorReportFilterPolicyConfig,
    ErrorReportFilterPolicyService,
    ErrorReportStackframeInput,
} from '@app/common/observability/domain/services/error-report-filter-policy.service';

interface BugsnagEventAdapter {
    severity: string;
    context?: string;
    addMetadata(section: string, metadata: Record<string, unknown>): void;
    errors?: Array<{
        errorClass?: string;
        errorMessage?: string;
        stacktrace?: Array<{
            file?: string;
            inProject?: boolean;
        }>;
    }>;
    request?: {
        url?: string;
    };
}

interface BugsnagWithOnError {
    addOnError?: (callback: (event: BugsnagEventAdapter) => boolean) => void;
}

@Injectable({
    providedIn: 'root',
})
export class BugsnagErrorReportingAdapter implements ErrorReportingPort {
    private static readonly ENABLED_RELEASE_STAGES: Array<string> = [
        'production',
        'staging',
        'develop',
    ];
    private static readonly DEFAULT_FIRST_PARTY_DOMAIN_SUFFIXES: Array<string> = [
        'saldo.com.ar',
        'saldoar.com',
        'localhost',
        '127.0.0.1',
    ];
    private static readonly DEFAULT_CRITICAL_THIRD_PARTY_DOMAIN_SUFFIXES: Array<string> = [
        'veriff.com',
        'veriff.me',
        'googleapis.com',
    ];

    private started: boolean = false;
    private onErrorFilterRegistered: boolean = false;
    private readonly errorReportFilterPolicy: ErrorReportFilterPolicyService;

    public constructor() {
        this.errorReportFilterPolicy = new ErrorReportFilterPolicyService(
            this.getErrorReportFilterPolicyConfig(),
        );
        this.initializeBugsnag();
    }

    public start(): void {
        this.initializeBugsnag();
    }

    private initializeBugsnag(): void {
        if (this.started || Bugsnag.isStarted()) {
            this.started = true;
            this.registerOnErrorFilter();

            return;
        }

        const apiKey: string = this.resolveApiKey();

        if (!apiKey) {
            console.warn('Bugsnag API key not found. Bugsnag will not be initialized.');

            return;
        }

        Bugsnag.start({
            apiKey: apiKey,
            appVersion: environment.version,
            releaseStage: environment.env,
            enabledReleaseStages: BugsnagErrorReportingAdapter.ENABLED_RELEASE_STAGES,
        });

        this.started = true;
        this.registerOnErrorFilter();

        console.info('Bugsnag initialized successfully');
    }

    public report(error: AppError | unknown, options?: ErrorReporterOptions): void {
        this.ensureStarted();

        if (!Bugsnag.isStarted()) {
            console.warn('Bugsnag not started. Error not reported.');

            return;
        }

        const normalizedError: Error | null = this.normalizeError(error);
        if (!normalizedError) {
            return;
        }

        Bugsnag.notify(normalizedError as NotifiableError, (event: BugsnagEventAdapter) => {
            if (options?.severity) {
                event.severity = options.severity;
            }

            if (options?.context) {
                event.context = options.context;
            }

            if (options?.metadata) {
                event.addMetadata('custom', options.metadata as Record<string, unknown>);
            }

            if (error instanceof AppError && error.detail_bag.length > 0) {
                event.addMetadata('error_details', {
                    details: error.detail_bag.map((detail) => ({
                        message: detail.message,
                        attribute: detail.attribute_name,
                    })),
                });
            }

            if (error instanceof AppError) {
                event.addMetadata('app_error', {
                    message: error.message,
                    render: error.render,
                    report: error.report,
                });
            }
        });
    }

    public setUser(user: ErrorReporterUser | null): void {
        this.ensureStarted();

        if (!Bugsnag.isStarted()) {
            return;
        }

        if (!user) {
            Bugsnag.setUser();

            return;
        }

        const id: string | undefined = user.id ?? undefined;
        const email: string | undefined = user.email ?? undefined;
        const name: string | undefined = user.name ?? undefined;

        if (!id && !email && !name) {
            Bugsnag.setUser();

            return;
        }

        Bugsnag.setUser(id, email, name);
    }

    public leaveBreadcrumb(
        message: string,
        metadata?: ErrorReporterMetadata,
        type?: ErrorReporterBreadcrumbType,
    ): void {
        this.ensureStarted();

        if (!Bugsnag.isStarted()) {
            return;
        }

        Bugsnag.leaveBreadcrumb(message, metadata as Record<string, unknown> | undefined, type);
    }

    private ensureStarted(): void {
        if (!this.started) {
            this.start();
        }
    }

    private resolveApiKey(): string {
        return typeof environment.TOKEN_BUGSNAG_APIKEY === 'string'
            ? environment.TOKEN_BUGSNAG_APIKEY.trim()
            : '';
    }

    private registerOnErrorFilter(): void {
        if (this.onErrorFilterRegistered) {
            return;
        }

        if (!Bugsnag.isStarted()) {
            return;
        }

        const bugsnagWithOnError: BugsnagWithOnError = Bugsnag as unknown as BugsnagWithOnError;
        if (typeof bugsnagWithOnError.addOnError !== 'function') {
            return;
        }

        bugsnagWithOnError.addOnError((event: BugsnagEventAdapter): boolean => {
            const filterInput: ErrorReportFilterInput = this.mapEventToFilterInput(event);
            const filterDecision: ReturnType<ErrorReportFilterPolicyService['evaluate']> =
                this.errorReportFilterPolicy.evaluate(filterInput);

            return filterDecision.shouldReport;
        });

        this.onErrorFilterRegistered = true;
    }

    private normalizeError(error: AppError | unknown): Error | null {
        if (error instanceof AppError) {
            if (error.original_error instanceof Error) {
                return error.original_error;
            }

            if (typeof error.original_error === 'string') {
                return new Error(error.original_error);
            }

            if (error.message) {
                return new Error(error.message);
            }
        }

        if (error instanceof Error) {
            return error;
        }

        if (typeof error === 'string') {
            return new Error(error);
        }

        if (typeof error === 'object' && error !== null) {
            try {
                return new Error(JSON.stringify(error));
            } catch {
                return new Error('Unknown error');
            }
        }

        return null;
    }

    private mapEventToFilterInput(event: BugsnagEventAdapter): ErrorReportFilterInput {
        const allErrors: NonNullable<BugsnagEventAdapter['errors']> = event.errors ?? [];
        const stacktrace: Array<ErrorReportStackframeInput> = [];

        allErrors.forEach((errorEntry) => {
            (errorEntry.stacktrace ?? []).forEach((stackframe) => {
                stacktrace.push({
                    file: stackframe.file,
                    inProject: stackframe.inProject,
                });
            });
        });

        return {
            errorClass: allErrors[0]?.errorClass,
            errorMessage: allErrors[0]?.errorMessage,
            requestUrl: event.request?.url,
            stacktrace,
        };
    }

    private getErrorReportFilterPolicyConfig(): ErrorReportFilterPolicyConfig {
        return {
            firstPartyDomainSuffixes: this.buildFirstPartyDomainSuffixes(),
            criticalThirdPartyDomainSuffixes:
                BugsnagErrorReportingAdapter.DEFAULT_CRITICAL_THIRD_PARTY_DOMAIN_SUFFIXES,
        };
    }

    private buildFirstPartyDomainSuffixes(): Array<string> {
        const candidates: Array<string> = [
            environment.API_URL,
            environment.CANONICAL_ORIGIN_URL,
            environment.LEGACYURL,
        ]
            .map((candidate) => this.tryExtractHostname(candidate))
            .filter((candidate): candidate is string => Boolean(candidate));

        const whiteListedDomains: Array<string> = Array.isArray(environment.WHITE_LISTED_DOMAINS)
            ? environment.WHITE_LISTED_DOMAINS.map((domain): string | null =>
                  typeof domain === 'string' ? this.normalizeDomainCandidate(domain) : null,
              ).filter((domain): domain is string => Boolean(domain))
            : [];

        const normalized: Set<string> = new Set(
            [
                ...BugsnagErrorReportingAdapter.DEFAULT_FIRST_PARTY_DOMAIN_SUFFIXES,
                ...candidates,
                ...whiteListedDomains,
            ].map((candidate) => candidate.toLowerCase()),
        );

        return Array.from(normalized);
    }

    private tryExtractHostname(urlLike: string | undefined): string | null {
        if (!urlLike) {
            return null;
        }

        try {
            const parsedUrl: URL = new URL(urlLike);

            return parsedUrl.hostname.toLowerCase();
        } catch {
            return null;
        }
    }

    private normalizeDomainCandidate(candidate: string): string | null {
        const normalizedCandidate: string = candidate.trim().toLowerCase();
        if (!normalizedCandidate) {
            return null;
        }

        if (
            normalizedCandidate.startsWith('http://') ||
            normalizedCandidate.startsWith('https://')
        ) {
            return this.tryExtractHostname(normalizedCandidate);
        }

        return normalizedCandidate.split('/')[0].split(':')[0];
    }
}
